import React, { useState } from "react";
import { Upload, Loader2, X, FileImage, RotateCcw, CheckCircle2, AlertCircle } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { parsePSDFileWithThumbnail } from "../../psd/parsePsd";
import { convertPSDToPostermakerTemplate } from "../../psd/postermakerTemplateMapper";
import { getCacheStats } from "../../psd/cacheManager";
import { useCategories } from "../../shared/hooks/useCategories";
import { useSnackbar } from "../../shared/hooks/useSnackbar";
import { useCategoryFormSync } from "../../shared/hooks/useCategoryFormSync";
import { CategorySelectors } from "../../shared/components/CategorySelectors";
import { TemplateMetadataFields } from "../../shared/components/TemplateMetadataFields";
import { FormSnackbar } from "../../shared/components/FormSnackbar";
import { parseCommaList, parseFetchError } from "../../shared/utils/formUtils";

interface FormDataType {
  psdFiles: File[];
  categories: string;
  subCategories: string;
  keywords: string;
  categorySlug: string;
  subcategorySlug: string;
  seoKeywords: string;
  namePrefix: string;
}

interface UploadError {
  error: string;
}

interface FileUploadStatus {
  file: File;
  status: 'pending' | 'processing' | 'uploading' | 'success' | 'error';
  progress: string;
  error?: string;
}

const AddPSDTemplateForm = () => {
  const {
    categories,
    loadingCategories,
    selectedCategory,
    selectedSubcategory,
    setCategories: setCategoriesFromHook,
    setSelectedCategory,
    setSelectedSubcategory,
  } = useCategories();
  
  const {
    showSnackbar,
    snackbarType,
    apiMessage,
    setShowSnackbar,
    showMessage,
  } = useSnackbar();

  const getInitialFormData = (): FormDataType => ({
    psdFiles: [],
    categories: "",
    subCategories: "",
    keywords: "",
    categorySlug: "",
    subcategorySlug: "",
    seoKeywords: "",
    namePrefix: "",
  });

  const [formData, setFormData] = useState<FormDataType>(getInitialFormData());

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [uploadError, setUploadError] = useState<UploadError | null>(null);
  const [conversionProgress, setConversionProgress] = useState<string>("");
  const [cacheInfo, setCacheInfo] = useState<string>("");
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);

  useCategoryFormSync(
    categories,
    selectedCategory,
    selectedSubcategory,
    setFormData
  );


  const validateFormData = (data: FormDataType): boolean => {
    return (
      data.psdFiles.length > 0 &&
      data.categories.trim() !== "" &&
      data.subCategories.trim() !== "" &&
      data.keywords.trim() !== "" &&
      data.categorySlug.trim() !== "" &&
      data.subcategorySlug.trim() !== "" &&
      data.seoKeywords.trim() !== ""
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate all files are PSD files
    const invalidFiles = files.filter(
      (file) =>
        file.type !== "image/vnd.adobe.photoshop" &&
        !file.name.toLowerCase().endsWith(".psd")
    );

    if (invalidFiles.length > 0) {
      setError(`Please upload only valid PSD files. ${invalidFiles.length} invalid file(s) found.`);
      showMessage(`Please upload only valid PSD files. ${invalidFiles.length} invalid file(s) found.`, "error");
      return;
    }

    setFormData((prev) => ({ ...prev, psdFiles: [...prev.psdFiles, ...files] }));
    
    // Initialize file statuses
    const newStatuses: FileUploadStatus[] = files.map((file) => ({
      file,
      status: 'pending',
      progress: 'Ready to upload'
    }));
    setFileStatuses((prev) => [...prev, ...newStatuses]);
    setError(null);
  };

  const handleRemoveFile = (index: number) => {
    setFormData((prev) => {
      const newFiles = [...prev.psdFiles];
      newFiles.splice(index, 1);
      return { ...prev, psdFiles: newFiles };
    });
    setFileStatuses((prev) => {
      const newStatuses = [...prev];
      newStatuses.splice(index, 1);
      return newStatuses;
    });
    setError(null);
  };

  const handleRemoveAllFiles = () => {
    setFormData((prev) => ({ ...prev, psdFiles: [] }));
    setFileStatuses([]);
    setError(null);
  };

  // Helper: Convert PSD file to template format
  const convertPSDFile = async (arrayBuffer: ArrayBuffer, fileName: string): Promise<any> => {
    let psdJson: any;
    let thumbUrl: string = "";
    try {
      const result = await parsePSDFileWithThumbnail(arrayBuffer, fileName);
      psdJson = result.json;
      thumbUrl = result.thumbUrl || "";
    } catch (parseError) {
      console.error("PSD parsing error:", parseError);
      throw new Error(
        parseError instanceof Error 
          ? parseError.message 
          : "Failed to parse PSD file"
      );
    }

    try {
      const convertedData = await convertPSDToPostermakerTemplate(psdJson, thumbUrl);
      return convertedData;
    } catch (convertError) {
      console.error("Template conversion error:", convertError);
      throw new Error(
        convertError instanceof Error 
          ? convertError.message 
          : "Failed to convert PSD to template format"
      );
    }
  };

  const uploadPSDTemplate = async (
    file: File,
    data: FormDataType,
    fileIndex: number
  ): Promise<void> => {
    // Update file status to processing
    setFileStatuses((prev) => {
      const updated = [...prev];
      updated[fileIndex] = {
        ...updated[fileIndex],
        status: 'processing',
        progress: 'Reading PSD file...'
      };
      return updated;
    });

    const arrayBuffer = await file.arrayBuffer();

    setFileStatuses((prev) => {
      const updated = [...prev];
      updated[fileIndex] = {
        ...updated[fileIndex],
        status: 'processing',
        progress: 'Processing PSD file...'
      };
      return updated;
    });

    const convertedData = await convertPSDFile(arrayBuffer, file.name);

    if (!convertedData?.layoutSize || !convertedData?.json_data) {
      throw new Error("Invalid conversion result. Missing required fields.");
    }

    const keywords = parseCommaList(data.keywords);
    const seoKeywords = parseCommaList(data.seoKeywords);

    // Generate template name from filename or use prefix
    const fileNameWithoutExt = file.name.replace(/\.psd$/i, '');
    const templateName = data.namePrefix.trim() 
      ? `${data.namePrefix.trim()} - ${fileNameWithoutExt}`
      : fileNameWithoutExt;

    setFileStatuses((prev) => {
      const updated = [...prev];
      updated[fileIndex] = {
        ...updated[fileIndex],
        status: 'uploading',
        progress: 'Uploading template...'
      };
      return updated;
    });

    // Prepare payload according to API specification
    const payload = {
      name: templateName,
      category: data.categories,
      subcategory: data.subCategories,
      keywords: keywords.join(","),
      categorySlug: data.categorySlug,
      subcategorySlug: data.subcategorySlug,
      seoKeywords: seoKeywords,
      templateData: {
        thumbUrl: convertedData.thumbUrl || "",
        layoutSize: convertedData.layoutSize,
        json_data: convertedData.json_data
      }
    };

    // Call the create-from-psd endpoint with converted template data
    const response = await fetch(
      getApiUrl("/api/templates/v1.0.0/template/create-from-psd"),
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      throw new Error(await parseFetchError(response));
    }

    return await response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setShowSnackbar(false);
    setUploadError(null);

    // Validate form data
    if (!validateFormData(formData)) {
      setError("Please fill in all required fields and upload at least one PSD file");
      showMessage("Please fill in all required fields and upload at least one PSD file", "error");
      return;
    }

    setIsLoading(true);
    setConversionProgress(`Processing ${formData.psdFiles.length} template(s)...`);

    let successCount = 0;
    let errorCount = 0;

    // Process each file sequentially
    for (let i = 0; i < formData.psdFiles.length; i++) {
      const file = formData.psdFiles[i];
      const result = await processFileUpload(file, i, formData);
      
      if (result.success) {
        successCount++;
      } else {
        errorCount++;
      }
    }

    // Show summary message
    if (errorCount === 0) {
      showMessage(`Successfully uploaded ${successCount} template(s)!`, "success");
      setFormData(getInitialFormData());
      setFileStatuses([]);
      setConversionProgress("");
    } else if (successCount > 0) {
      showMessage(`Uploaded ${successCount} template(s), ${errorCount} failed.`, "error");
    } else {
      showMessage(`Failed to upload ${errorCount} template(s).`, "error");
    }

    setIsLoading(false);
  };

  const handleInputChange = (field: string, value: string) => {
    // Clear error and success messages when user starts typing
    if (error || success || showSnackbar) {
      setError(null);
      setSuccess(false);
      setShowSnackbar(false);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper: Get file status classes
  const getFileStatusClasses = (isSuccess: boolean, isError: boolean, isProcessing: boolean) => {
    if (isSuccess) return 'border-green-300 bg-green-50';
    if (isError) return 'border-red-300 bg-red-50';
    if (isProcessing) return 'border-blue-300 bg-blue-50';
    return 'border-indigo-300 bg-indigo-50';
  };

  // Helper: Get icon container classes
  const getIconContainerClasses = (isSuccess: boolean, isError: boolean, isProcessing: boolean) => {
    if (isSuccess) return 'bg-green-100';
    if (isError) return 'bg-red-100';
    if (isProcessing) return 'bg-blue-100';
    return 'bg-indigo-100';
  };

  // Helper: Get file status icon
  const getFileStatusIcon = (isSuccess: boolean, isError: boolean, isProcessing: boolean) => {
    if (isSuccess) return <CheckCircle2 className="w-5 h-5 text-green-600" />;
    if (isError) return <AlertCircle className="w-5 h-5 text-red-600" />;
    if (isProcessing) return <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />;
    return <FileImage className="w-5 h-5 text-indigo-600" />;
  };

  // Helper: Get progress text classes
  const getProgressTextClasses = (isSuccess: boolean, isError: boolean, isProcessing: boolean) => {
    if (isSuccess) return 'text-green-700';
    if (isError) return 'text-red-700';
    if (isProcessing) return 'text-blue-700';
    return 'text-gray-600';
  };

  // Helper: Process a single file upload
  const processFileUpload = async (
    file: File,
    fileIndex: number,
    formData: FormDataType
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await uploadPSDTemplate(file, formData, fileIndex);
      
      // Update status to success
      setFileStatuses((prev) => {
        const updated = [...prev];
        updated[fileIndex] = {
          ...updated[fileIndex],
          status: 'success',
          progress: 'Uploaded successfully'
        };
        return updated;
      });
      return { success: true };
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unexpected error occurred";
      
      // Update status to error
      setFileStatuses((prev) => {
        const updated = [...prev];
        updated[fileIndex] = {
          ...updated[fileIndex],
          status: 'error',
          progress: 'Upload failed',
          error: errorMessage
        };
        return updated;
      });
      console.error(`Upload failed for ${file.name}:`, errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const handleReset = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    setFormData(getInitialFormData());
    setFileStatuses([]);
    setSelectedCategory("");
    setSelectedSubcategory("");
    setError(null);
    setSuccess(false);
    setUploadError(null);
    setConversionProgress("");
    setCacheInfo("");
    setShowSnackbar(false);
    // Reset file input
    const fileInput = document.getElementById("psdFiles") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white my-8 p-8 rounded-2xl shadow-lg border border-gray-100">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h3 className="text-2xl font-bold text-gray-800">
              Add PSD Template(s)
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Upload one or multiple PSD files and enter template details
            </p>
          </div>
          <div className="flex gap-3 items-center">
            {/* Reset Button - Circular */}
            <button
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="w-10 h-10 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-md hover:shadow-lg"
              title="Reset all form fields"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
            {/* Cache Stats */}
            <div className="px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs">
              <span className="text-indigo-700 font-medium">
                Cache: {getCacheStats().images} images
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* PSD File Upload */}
          <div>
            <label
              htmlFor="psdFiles"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              PSD File(s) * {formData.psdFiles.length > 0 && `(${formData.psdFiles.length} selected)`}
            </label>
            {formData.psdFiles.length > 0 ? (
              <div className="space-y-3">
                {formData.psdFiles.map((file, index) => {
                  const fileStatus = fileStatuses[index];
                  const isProcessing = fileStatus?.status === 'processing' || fileStatus?.status === 'uploading';
                  const isSuccess = fileStatus?.status === 'success';
                  const isError = fileStatus?.status === 'error';
                  
                  const statusClasses = getFileStatusClasses(isSuccess, isError, isProcessing);
                  const iconClasses = getIconContainerClasses(isSuccess, isError, isProcessing);
                  const progressClasses = getProgressTextClasses(isSuccess, isError, isProcessing);
                  const statusIcon = getFileStatusIcon(isSuccess, isError, isProcessing);
                  
                  return (
                    <div
                      key={`${file.name}-${index}`}
                      className={`border-2 rounded-xl p-4 ${statusClasses}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconClasses}`}>
                            {statusIcon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            {fileStatus && (
                              <p className={`text-xs mt-1 ${progressClasses}`}>
                                {fileStatus.progress}
                              </p>
                            )}
                            {isError && fileStatus?.error && (
                              <p className="text-xs text-red-600 mt-1">
                                {fileStatus.error}
                              </p>
                            )}
                          </div>
                        </div>
                        {!isProcessing && (
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(index)}
                            className="p-2 hover:bg-red-100 rounded-lg transition-colors ml-2"
                            title="Remove file"
                            disabled={isLoading}
                          >
                            <X className="w-5 h-5 text-red-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {formData.psdFiles.length > 0 && !isLoading && (
                  <button
                    type="button"
                    onClick={handleRemoveAllFiles}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Remove all files
                  </button>
                )}
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-indigo-500 transition-colors">
                  <input
                    id="psdFiles"
                    type="file"
                    accept=".psd,image/vnd.adobe.photoshop"
                    onChange={handleFileChange}
                    className="hidden"
                    multiple
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="psdFiles"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mb-2">
                      <Upload className="w-6 h-6 text-indigo-600" />
                    </div>
                    <p className="text-sm text-gray-700 font-medium">
                      Add more PSD files
                    </p>
                  </label>
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-indigo-500 transition-colors">
                <input
                  id="psdFiles"
                  type="file"
                  accept=".psd,image/vnd.adobe.photoshop"
                  onChange={handleFileChange}
                  className="hidden"
                  multiple
                  disabled={isLoading}
                />
                <label
                  htmlFor="psdFiles"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                    <FileImage className="w-8 h-8 text-indigo-600" />
                  </div>
                  <p className="text-gray-700 font-medium mb-1">
                    Click to upload PSD file(s)
                  </p>
                  <p className="text-sm text-gray-500">
                    Upload one or multiple Photoshop PSD files
                  </p>
                </label>
              </div>
            )}
          </div>

          {/* Template Name Prefix */}
          <div>
            <label
              htmlFor="namePrefix"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Template Name Prefix {formData.psdFiles.length > 1 && <span className="text-gray-500 font-normal">(optional)</span>}
            </label>
            <input
              id="namePrefix"
              type="text"
              value={formData.namePrefix}
              onChange={(e) => handleInputChange("namePrefix", e.target.value)}
              placeholder={formData.psdFiles.length > 1 ? "e.g., Collection Name (will be: 'Collection Name - filename.psd')" : "e.g., My PSD Template"}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
            {formData.psdFiles.length > 1 && (
              <p className="text-xs text-gray-500 mt-1">
                If provided, template names will be: "{formData.namePrefix || 'Prefix'} - [filename]". 
                If empty, filenames will be used as template names.
              </p>
            )}
          </div>

          <CategorySelectors
            categories={categories}
            loadingCategories={loadingCategories}
            selectedCategory={selectedCategory}
            selectedSubcategory={selectedSubcategory}
            onCategoryChange={(categoryId) => {
              setSelectedCategory(categoryId);
              setSelectedSubcategory("");
            }}
            onSubcategoryChange={setSelectedSubcategory}
            setCategories={setCategoriesFromHook}
            setLoadingCategories={() => {}}
            onRefreshCategories={() => {}}
          />

          <TemplateMetadataFields
            keywords={formData.keywords}
            categorySlug={formData.categorySlug}
            subcategorySlug={formData.subcategorySlug}
            seoKeywords={formData.seoKeywords}
            onKeywordsChange={(v) => handleInputChange("keywords", v)}
            onCategorySlugChange={(v) => handleInputChange("categorySlug", v)}
            onSubcategorySlugChange={(v) => handleInputChange("subcategorySlug", v)}
            onSeoKeywordsChange={(v) => handleInputChange("seoKeywords", v)}
          />

          {/* Cache Info */}
          {cacheInfo && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="text-sm text-green-700 font-medium">{cacheInfo}</p>
            </div>
          )}

          {/* Conversion Progress */}
          {conversionProgress && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm text-blue-700">{conversionProgress}</p>
            </div>
          )}

          {/* Upload Error Display */}
          {uploadError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">
                <strong>Error:</strong> {uploadError.error}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 shadow-lg ${
                isLoading
                  ? "bg-gray-400 cursor-not-allowed text-white"
                  : "bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
              }`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{conversionProgress || `Uploading ${formData.psdFiles.length} PSD Template(s)...`}</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  <span>Upload {formData.psdFiles.length > 1 ? `${formData.psdFiles.length} PSD Templates` : 'PSD Template'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <FormSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          setError(null);
          setSuccess(false);
        }}
        type={snackbarType}
        error={error}
        apiMessage={apiMessage}
        successMessage="PSD template added successfully! 🎉"
      />
    </div>
  );
};

export default AddPSDTemplateForm;
